import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell";
import { AppleIcon, GooglePlayIcon } from "./MobileAppInviteModal";
import { isNativeApp } from "../../native/runtime";
import {
  dismissOptionalMobileUpdate,
  MOBILE_UPDATE_STATUS,
  openMobileStoreListing,
  readMobileUpdatePolicy,
  wasOptionalMobileUpdateDismissed,
} from "../../native/mobileUpdate";

function MobileUpdateIcon({ platform }) {
  const StoreIcon = platform === "android" ? GooglePlayIcon : AppleIcon;
  return (
    <div
      className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white"
      aria-hidden="true"
    >
      <StoreIcon className="h-8 w-8" />
    </div>
  );
}

export function MobileUpdatePrompt({ state, onDismiss, onOpenStore = openMobileStoreListing }) {
  const { t } = useTranslation("common");
  const [openingStore, setOpeningStore] = useState(false);
  const [storeError, setStoreError] = useState(false);
  const required = state.status === MOBILE_UPDATE_STATUS.REQUIRED;
  const platform = state.policy?.platform;
  const storeName = t(platform === "android" ? "mobileUpdate.googlePlay" : "mobileUpdate.appStore");

  const handleOpenStore = async () => {
    if (!state.policy?.storeUrl || openingStore) return;
    setOpeningStore(true);
    setStoreError(false);
    try {
      await onOpenStore(state.policy.platform, state.policy.storeUrl);
    } catch (error) {
      console.warn("Could not open the mobile app store", error);
      setStoreError(true);
    } finally {
      setOpeningStore(false);
    }
  };

  return (
    <AppModalShell
      open
      title={t(required ? "mobileUpdate.requiredTitle" : "mobileUpdate.availableTitle")}
      description={t(
        required
          ? "mobileUpdate.requiredDescription"
          : "mobileUpdate.availableDescription",
        { storeName }
      )}
      onClose={onDismiss}
      closeAriaLabel={t("buttons.close")}
      maxWidthClassName="max-w-[480px]"
      zIndexClassName="z-[15000]"
      forcePortalToBody
      footer={
        <AppModalButton onClick={handleOpenStore} loading={openingStore}>
          {t(openingStore ? "mobileUpdate.openingStore" : "mobileUpdate.updateButton", { storeName })}
        </AppModalButton>
      }
    >
      <AppModalContentItem className="pb-3 pt-1">
        <MobileUpdateIcon platform={platform} />
      </AppModalContentItem>
      {storeError ? (
        <AppModalContentItem
          role="alert"
          className="mt-2 rounded-2xl border border-red-300/20 bg-red-300/10 px-4 py-3 text-center text-[14px] font-medium leading-5 text-red-100"
        >
          {t("mobileUpdate.storeError")}
        </AppModalContentItem>
      ) : null}
    </AppModalShell>
  );
}

export default function MobileUpdateController({ children }) {
  const { t } = useTranslation("common");
  const [state, setState] = useState({
    status: MOBILE_UPDATE_STATUS.NONE,
    currentBuild: null,
    policy: null,
    checking: isNativeApp(),
    initialized: !isNativeApp(),
  });
  const [sessionDismissedBuild, setSessionDismissedBuild] = useState(null);
  const [sessionDismissedRequiredBuild, setSessionDismissedRequiredBuild] = useState(null);
  const checkPromiseRef = useRef(null);

  const checkForUpdate = useCallback(() => {
    if (!isNativeApp()) return Promise.resolve();
    if (checkPromiseRef.current) return checkPromiseRef.current;

    setState((previous) => ({ ...previous, checking: true }));
    const request = readMobileUpdatePolicy()
      .then((result) => {
        setState({
          status: result.status,
          currentBuild: result.currentBuild,
          policy: result.policy,
          checking: false,
          initialized: true,
        });
      })
      .catch((error) => {
        // Fail open when no policy has been observed. If an update was already
        // required in this session, retain the gate across transient failures.
        console.warn("Mobile app update check failed", error);
        setState((previous) => ({
          ...previous,
          checking: false,
          initialized: true,
        }));
      })
      .finally(() => {
        checkPromiseRef.current = null;
      });

    checkPromiseRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    void checkForUpdate();
    const handleResume = () => {
      void checkForUpdate();
    };
    window.addEventListener("vg:native-resume", handleResume);
    return () => window.removeEventListener("vg:native-resume", handleResume);
  }, [checkForUpdate]);

  const dismissOptionalUpdate = () => {
    const platform = state.policy?.platform;
    const latestBuild = state.policy?.latestBuild;
    if (!platform || !latestBuild) return;
    dismissOptionalMobileUpdate(platform, latestBuild);
    setSessionDismissedBuild(latestBuild);
  };

  const dismissUpdate = () => {
    if (state.status === MOBILE_UPDATE_STATUS.REQUIRED) {
      setSessionDismissedRequiredBuild(state.policy?.latestBuild);
      return;
    }
    dismissOptionalUpdate();
  };

  const optionalPromptDismissed =
    state.status === MOBILE_UPDATE_STATUS.OPTIONAL &&
    (sessionDismissedBuild === state.policy?.latestBuild ||
      wasOptionalMobileUpdateDismissed(
        state.policy?.platform,
        state.policy?.latestBuild
      ));
  const showPrompt =
    (state.status === MOBILE_UPDATE_STATUS.REQUIRED &&
      sessionDismissedRequiredBuild !== state.policy?.latestBuild) ||
    (state.status === MOBILE_UPDATE_STATUS.OPTIONAL && !optionalPromptDismissed);

  useEffect(() => {
    if (!isNativeApp()) return;
    const visible = !state.initialized || showPrompt;
    window.__VG_MOBILE_UPDATE_VISIBLE__ = visible;
    window.dispatchEvent(new CustomEvent("vg:mobile-update-state", { detail: { visible } }));
  }, [state.initialized, showPrompt]);

  return (
    <>
      {children}
      {!state.initialized ? (
        <div
          className="fixed inset-0 z-[15000] bg-[#050812] bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/launch/vaultgrid-launch-static.png')" }}
          role="status"
          aria-busy="true"
          aria-label={t("mobileUpdate.checking")}
        />
      ) : null}
      {showPrompt ? (
        <MobileUpdatePrompt
          state={state}
          onDismiss={dismissUpdate}
        />
      ) : null}
    </>
  );
}
